<script lang="ts" setup>
import { defineAsyncComponent, onMounted, ref, watch } from "vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VLabel from "@magnit/core/src/components/VLabel/VLabel.vue";
import { useAnalyticManager } from "@magnit/analytic-events";
import CircleHumanIcon from "@magnit/icons/src/assets/icons/circle-human-24.svg";
import EntranceIcon from "@magnit/icons/src/assets/svg/icons/base/24-outline-entrance.svg";
import { timeout } from "@magnit/core/src/utilities/async";
import type { TLabelTheme } from "@magnit/core/src/components/VLabel/VLabel.types";

// vue3-lottie при импорте сразу ломиться создавать canvas context,
// ну т.е. эта шляпа вообще никак про SSR не думала...
// Поэтому мы его грузим исключительно динамическим импортом, только при первой отрисовке компонента его будет загружать.
// А отрисовываем мы его только когда стартует анимация. Анимация предусматривает старт только на клиенте.
// Лотти один, а костылей тысячи...
const Vue3Lottie = defineAsyncComponent(
  async () => (await import("vue3-lottie"))?.Vue3Lottie,
);

const props = defineProps<{
  authorized?: boolean;
  username?: string;
  premium?: boolean;
}>();

const emit = defineEmits<{
  click: [];
}>();

const { send } = useAnalyticManager();

const isNameVisible = ref(true);
const lottiePremiumPlaying = ref(false);
const lottieAnimationData = ref();
const loadLottieAnimation = async () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  if (lottieAnimationData.value) {
    return lottieAnimationData.value;
  }
  try {
    const data = await import(
      "@magnit/unit-header/src/assets/lottie/profile-premium-animation.json"
    );
    if (data) {
      // библиотека vue лотти дурачок, она мутирует входные данные анимации... поэтому делаем мутабельный объект спешл фо ит
      lottieAnimationData.value = { ...data };
      return lottieAnimationData.value;
    }
  } catch (e) {
    console.error("Error while loading premium animation data:", e);
    return undefined;
  }
};

const playPremiumAnimation = async () => {
  if (typeof window === "undefined" || !props.premium) {
    return;
  }
  if (!isNameVisible.value || lottiePremiumPlaying.value) {
    return; // анимация уже играется, не играем её дважды+
  }
  if (!(await loadLottieAnimation())) {
    stopPremiumAnimation();
    return;
  }
  // небольшая задержка перед анимациями в целом,
  // чтобы страница успела там чёнить закрыть перерисовать и пр.
  // и чтобы пользователь мог осознать себя перед тем как анимации начнут проигрываться :D
  // возможно этот параметр потом потвикаем до полного удовольствия
  await timeout(500);
  isNameVisible.value = false;
  await timeout(300); // на то время когда проигрывается анимация скрытия имени
  lottiePremiumPlaying.value = true;
};
const stopPremiumAnimation = () => {
  isNameVisible.value = true;
  lottiePremiumPlaying.value = false;
};

const onAuthClick = () => {
  send("Toolbar:Auth:Click");
  emit("click");
};

onMounted(() => {
  send("Toolbar:Auth:View");
  playPremiumAnimation();
});

watch(
  () => props.premium,
  () => {
    playPremiumAnimation();
  },
);
</script>

<template>
  <div class="pl-auth-button">
    <template v-if="authorized">
      <VHidden :hide="{ xs: false, m: true }">
        <VButton
          theme="invert"
          icon-position="right"
          full-width
          data-test-id="auth-button-authorized-mobile"
          @click="onAuthClick"
        >
          <template v-if="username" #default>
            <Transition name="slide">
              <VText
                v-if="isNameVisible"
                class="username"
                font="body-large-accent"
                >{{ username }}</VText
              >
            </Transition>
          </template>
          <template #icon>
            <div
              v-if="lottieAnimationData && lottiePremiumPlaying"
              style="margin-right: -8px"
            >
              <Vue3Lottie
                :animation-data="lottieAnimationData"
                :height="44"
                :loop="false"
                @on-complete="stopPremiumAnimation"
              />
            </div>
            <CircleHumanIcon v-else />
          </template>
        </VButton>
      </VHidden>

      <VHidden :hide="{ xs: true, m: false }">
        <VButton
          theme="border"
          icon-position="left"
          aria-label="Профиль"
          full-width
          data-test-id="auth-button-authorized"
          @click="onAuthClick"
        >
          <template v-if="username" #default>
            <div class="name-stack">
              <VText class="username" font="caption-large-accent-low">{{
                username
              }}</VText>
              <VLabel
                v-if="premium"
                size="xs"
                :theme="'premium' satisfies TLabelTheme"
                >премиум</VLabel
              >
              <VText v-else font="caption-large-accent-low" color="secondary"
                >Профиль</VText
              >
            </div>
          </template>
          <template #icon>
            <CircleHumanIcon />
          </template>
        </VButton>
      </VHidden>
    </template>
    <template v-else>
      <!-- !authorized -->
      <VHidden :hide="{ xs: false, m: true }">
        <VButton
          theme="invert"
          icon-position="right"
          aria-label="Войти"
          data-test-id="auth-button-unauthorized-mobile"
          @click="onAuthClick"
        >
          <template #default>
            <VText font="body-large-accent">Войти</VText>
          </template>
          <template #icon>
            <EntranceIcon />
          </template>
        </VButton>
      </VHidden>
      <VHidden :hide="{ xs: true, m: false }">
        <VButton
          theme="border"
          icon-position="left"
          aria-label="Войти"
          data-test-id="auth-button-unauthorized"
          @click="onAuthClick"
        >
          <template #default>
            <VText font="body-large-accent">Войти</VText>
          </template>
          <template #icon>
            <EntranceIcon />
          </template>
        </VButton>
      </VHidden>
    </template>
  </div>
</template>

<style scoped lang="postcss">
.slide-enter-from {
  opacity: 0;
  left: 100%;
}

.slide-enter-to {
  opacity: 1;
  left: 0;
}

.slide-leave-from {
  opacity: 1;
  left: 0;
}

.slide-leave-to {
  opacity: 0;
  left: 100%;
}

.slide-enter-active,
.slide-leave-active {
  position: relative;
  transition:
    left 300ms ease,
    opacity 300ms ease;
}

@media (--pl-viewport-from-m) {
  .pl-auth-button {
    max-width: 120px;
  }
}

.name-stack {
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  align-items: start;
}

.pl-auth-button,
.pl-auth-button :deep(.pl-button-base__content),
.pl-auth-button :deep(.pl-button__text) {
  width: 100%;
}

.pl-auth-button :deep(.pl-button__title) {
  display: flex;
}

.username {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: start;
}
</style>
